import React, { useContext, useCallback, useMemo, useState, useEffect, useRef } from "react";
import { Footer as MDSFooter } from "@web-monorepo/mds";
import { getRelativePath } from "@src/utils/routes";
import { logEvent } from "@src/utils/logClient";
import { TranslationContext } from "@src/components/translation/TranslationContext";
import { FooterLanguageTrigger } from "./FooterLanguageTrigger";
import isMobile from "@src/utils/isMobile";

/**
 * Safely convert a translation result to a string for MDS Footer labels.
 * The translate() function returns string | JSX.Element | JSX.Element[].
 * For simple text translations (no HTML), it returns a plain string.
 */
function translationToString(value: string | JSX.Element | JSX.Element[] | null | undefined): string {
  if (value == null) return "";
  if (typeof value === "string") return value;
  return "";
}

type FooterProps = { isBlog?: boolean; isIdea?: boolean };

function isAppleDevice() {
  if (typeof window === "undefined" || typeof navigator === "undefined") return false;
  const isApple = isMobile().apple.device;
  if (isApple) return true;
  if (typeof window.location !== "undefined" && window.location.search.includes("ios=true")) return true;
  return false;
}

type FooterItemDef = {
  link: string;
  isInternal: boolean;
  doesTranslate: boolean;
  string: string;
  id: string;
  condition?: boolean;
};

type FooterColumnDef = {
  id: string;
  titleKey: string;
  condition?: boolean;
  items: FooterItemDef[];
};

const COOKIE_SETTINGS_HREF = "#cookie-settings";

const Footer: React.FC<FooterProps> = ({ isBlog, isIdea }) => {
  const footerRef = useRef<HTMLDivElement>(null);
  const { translate, getIsCountryUS } = useContext(TranslationContext);
  const isCountryUS = getIsCountryUS();

  // Track whether to hide Google Play — starts false (show both), updates after mount
  const [hideGooglePlay, setHideGooglePlay] = useState(false);

  useEffect(() => {
    if (isAppleDevice()) {
      setHideGooglePlay(true);
    }
  }, []);

  // After MDS Footer renders, tag the cookie settings link with OneTrust attributes
  useEffect(() => {
    const footerEl = footerRef.current;
    if (!footerEl) return;
    const cookieLink = footerEl.querySelector(`a[href="${COOKIE_SETTINGS_HREF}"]`);
    if (cookieLink) {
      cookieLink.id = "ot-sdk-btn";
      cookieLink.classList.add("ot-sdk-show-settings", "animated-underline");
    }
  });

  const returnRightLink = useCallback(
    (isInternal: boolean, pathlink: string) => {
      if (!isInternal) {
        return pathlink;
      } else if (isBlog || isIdea) {
        return `https://www.classdojo.com${pathlink}`;
      } else {
        return getRelativePath(pathlink);
      }
    },
    [isBlog, isIdea],
  );

  const resolveLabel = useCallback(
    (item: FooterItemDef) => {
      if (item.doesTranslate) {
        return translationToString(translate(item.string));
      }
      return item.string;
    },
    [translate],
  );

  // Build the footer data structure
  // Each column has raw items with translation keys, conditions, and link metadata
  const footerColumns = useMemo<FooterColumnDef[]>(
    () => [
      {
        id: "company",
        titleKey: "footer.menu.1.title",
        items: [
          { link: "/about/", isInternal: true, doesTranslate: true, string: "layouts.main.aboutus", id: "about_us" },
          { link: "/press/", isInternal: true, doesTranslate: true, string: "layouts.main.press", id: "press" },
          { link: "/jobs/", isInternal: true, doesTranslate: true, string: "layouts.main.careers", id: "jobs" },
          {
            link: "https://engineering.classdojo.com",
            isInternal: false,
            doesTranslate: true,
            string: "layouts.main.engineering",
            id: "engineering",
          },
          {
            link: "/accessibility/",
            isInternal: true,
            doesTranslate: true,
            string: "layouts.main.accessibility",
            id: "accessibility",
          },
        ] as FooterItemDef[],
      },
      {
        id: "resources",
        titleKey: "footer.menu.2.title",
        items: [
          {
            link: "https://ideas.classdojo.com",
            isInternal: false,
            doesTranslate: true,
            string: "layouts.main.bigideas",
            id: "big_ideas",
          },
          { link: "/points", isInternal: true, doesTranslate: true, string: "layouts.main.points", id: "points" },
          {
            link: "/resources/",
            isInternal: true,
            doesTranslate: true,
            string: "layouts.main.support.resources",
            id: "resources",
          },
          {
            link: "/training/",
            isInternal: true,
            doesTranslate: true,
            string: "footer.menu.2.training",
            id: "training",
          },
          {
            link: "/remotelearning/",
            isInternal: true,
            doesTranslate: true,
            string: "footer.menu.2.remote_learning",
            id: "remoteLearning",
          },
          {
            link: "/plus/",
            isInternal: true,
            doesTranslate: false,
            string: "ClassDojo Plus",
            id: "plus",
          },
          {
            link: "https://tutor.classdojo.com/",
            isInternal: false,
            doesTranslate: false,
            string: "Dojo Tutor",
            id: "tutor",
            condition: isCountryUS,
          },
          {
            link: "/activity-corner/",
            isInternal: true,
            doesTranslate: true,
            string: "layouts.main.submenu_activity_corner.cta",
            id: "activity_corner",
          },
        ] as FooterItemDef[],
      },
      {
        id: "support",
        titleKey: "footer.menu.3.title",
        items: [
          {
            link: "https://help.classdojo.com",
            isInternal: false,
            doesTranslate: true,
            string: "layouts.main.helpdesk",
            id: "helpdesk",
          },
          {
            link: "/contact/",
            isInternal: true,
            doesTranslate: true,
            string: "layouts.main.contact",
            id: "contact",
          },
          {
            link: COOKIE_SETTINGS_HREF,
            isInternal: true,
            doesTranslate: true,
            string: "footer.menu.3.cookie_settings",
            id: "ot-sdk-btn",
          },
          {
            link: "/transparency/",
            isInternal: true,
            doesTranslate: true,
            string: "layouts.main.notice_at_collection",
            id: "notice-at-collect",
          },
          {
            link: "/privacy-and-security/",
            isInternal: true,
            doesTranslate: true,
            string: "layouts.main.privacy_and_security",
            id: "privacy-and-security",
          },
        ] as FooterItemDef[],
      },
      {
        id: "community",
        titleKey: "footer.menu.4.title",
        items: [
          {
            link: "https://www.facebook.com/groups/classdojoteachers",
            isInternal: false,
            doesTranslate: true,
            string: "footer.menu.4.2",
            id: "fb_community",
          },
          {
            link: "/wall-of-love/",
            isInternal: true,
            doesTranslate: true,
            string: "footer.menu.4.1",
            id: "wallOfLove",
          },
        ] as FooterItemDef[],
      },
      {
        id: "tutoring",
        titleKey: "footer.menu.5.title",
        condition: isCountryUS,
        items: [
          { link: "/tutoring", isInternal: true, doesTranslate: false, string: "Find a tutor", id: "find_a_tutor" },
          {
            link: "/tutoring/math",
            isInternal: true,
            doesTranslate: false,
            string: "Math tutoring",
            id: "math_tutoring",
          },
          {
            link: "/tutoring/reading",
            isInternal: true,
            doesTranslate: false,
            string: "Reading tutoring",
            id: "reading_tutoring",
          },
          {
            link: "/tutoring/dyslexia",
            isInternal: true,
            doesTranslate: false,
            string: "Dyslexia tutors",
            id: "dyslexia_tutors",
          },
          { link: "/tutoring/esl", isInternal: true, doesTranslate: false, string: "ESL tutors", id: "esl_tutors" },
        ] as FooterItemDef[],
      },
    ],
    [isCountryUS],
  );

  const privacyLinksDef = useMemo(
    () => [
      { string: "layouts.main.terms", link: "/terms/", id: "terms", isInternal: true },
      { string: "layouts.main.privacy", link: "/privacy/", id: "privacy", isInternal: true },
      { string: "layouts.main.web_privacy", link: "/website-privacy/", id: "web_privacy", isInternal: true },
    ],
    [],
  );

  // Pre-resolve all data into MDS FooterProps format
  // Note: No isClient guard in filter — isCountryUS is derived from locale (available during SSR)
  const menus = useMemo(
    () =>
      footerColumns
        .filter((column) => column.condition === undefined || column.condition)
        .map((column) => ({
          title: translationToString(translate(column.titleKey)),
          items: column.items
            .filter((item) => {
              if (item.condition !== undefined) {
                return item.condition;
              }
              return true;
            })
            .map((item) => ({
              label: resolveLabel(item),
              href:
                item.link === COOKIE_SETTINGS_HREF ? COOKIE_SETTINGS_HREF : returnRightLink(item.isInternal, item.link),
            })),
        })),
    [footerColumns, translate, resolveLabel, returnRightLink],
  );

  const smallLinks = useMemo(
    () =>
      privacyLinksDef.map((item) => ({
        label: translationToString(translate(item.string)),
        href: returnRightLink(item.isInternal, item.link),
      })),
    [privacyLinksDef, translate, returnRightLink],
  );

  // Social links: Facebook, X (Twitter), Instagram moved from community column
  const socialLinks = useMemo(
    () => [
      { label: "Facebook", href: "https://www.facebook.com/classdojo", icon: "facebook" as const },
      { label: "X", href: "https://www.twitter.com/classdojo", icon: "x" as const },
      { label: "Instagram", href: "https://www.instagram.com/classdojo", icon: "instagram" as const },
    ],
    [],
  );

  // App store links — always include both initially (avoids hydration mismatch),
  // then remove Google Play after mount if on Apple device
  const appStoreLinks = useMemo(() => {
    const links: { label: string; href: string; icon: "ios" | "google" }[] = [
      {
        label: "App Store",
        href: "https://itunes.apple.com/us/app/classdojo/id552602056?mt=8",
        icon: "ios",
      },
      {
        label: "Google Play",
        href: "https://play.google.com/store/apps/details?id=com.classdojo.android",
        icon: "google",
      },
    ];

    if (hideGooglePlay) {
      return links.filter((l) => l.icon !== "google");
    }

    return links;
  }, [hideGooglePlay]);

  // Build a lookup map from resolved href → event ID for click tracking
  const hrefToEventId = useMemo(() => {
    const map: Record<string, string> = {};

    footerColumns
      .filter((column) => column.condition === undefined || column.condition)
      .forEach((column) => {
        column.items.forEach((item) => {
          if (item.condition !== undefined && !item.condition) return;
          const href =
            item.link === COOKIE_SETTINGS_HREF ? COOKIE_SETTINGS_HREF : returnRightLink(item.isInternal, item.link);
          map[href] = item.id;
        });
      });

    privacyLinksDef.forEach((item) => {
      const href = returnRightLink(item.isInternal, item.link);
      map[href] = item.id;
    });

    // Social links
    map["https://www.facebook.com/classdojo"] = "facebook";
    map["https://www.twitter.com/classdojo"] = "twitter";
    map["https://www.instagram.com/classdojo"] = "instagram";

    // App store links
    map["https://itunes.apple.com/us/app/classdojo/id552602056?mt=8"] = "appStore";
    map["https://play.google.com/store/apps/details?id=com.classdojo.android"] = "playStore";

    return map;
  }, [footerColumns, privacyLinksDef, returnRightLink]);

  // Event delegation handler for footer click tracking
  const handleFooterClick = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      const target = e.target as HTMLElement;
      const anchor = target.closest("a");
      if (!anchor) return;

      const href = anchor.getAttribute("href");
      if (!href) return;

      // Prevent default navigation for the cookie settings link
      if (href === COOKIE_SETTINGS_HREF) {
        e.preventDefault();
      }

      const eventId = hrefToEventId[href];
      if (eventId) {
        logEvent({
          eventName: `web.external_page.footer.click.${eventId}`,
        });
      }
    },
    [hrefToEventId],
  );

  return (
    // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions
    <div ref={footerRef} onClick={handleFooterClick} className="print:hidden">
      <MDSFooter
        title="ClassDojo"
        description={
          translationToString(translate("footer.mission")) ||
          "Our mission is to give every child an education they love."
        }
        menus={menus}
        copyright="© ClassDojo, Inc"
        smallLinks={smallLinks}
        socialLinks={socialLinks}
        appStoreLinks={appStoreLinks}
        languageSelect={!isBlog ? <FooterLanguageTrigger /> : undefined}
        accessibilityLink="https://www.essentialaccessibility.com/class-dojo?utm_source=classdojohomepage&utm_medium=iconlarge&utm_term=eachannelpage&utm_content=header&utm_campaign=classdojo"
      />
    </div>
  );
};

export default Footer;
